import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { shopAxios } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { ValidationError } from '../admin/components/ValidationError';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';

function tx(t: (k: string) => string, key: string, fallback: string): string {
    const v = t(key);
    return !v || v === key ? fallback : v;
}

/** Shop-only password reset from email link. No auto-login — user signs in after. */
const ResetPassword = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const userId = searchParams.get('userId') ?? '';
    const token = searchParams.get('token') ?? '';

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const linkInvalid = !userId || !token;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        if (linkInvalid) {
            setError(tx(t, 'page.resetPassword.invalidLink', 'This reset link is invalid or incomplete.'));
            return;
        }
        if (password.length < 8) {
            setError(tx(t, 'page.resetPassword.passwordTooShort', 'Password must be at least 8 characters.'));
            scrollToFirstErrorField();
            return;
        }
        if (password !== confirm) {
            setError(tx(t, 'page.resetPassword.passwordMismatch', 'Passwords do not match.'));
            scrollToFirstErrorField();
            return;
        }

        setSubmitting(true);
        try {
            await shopAxios.post('/auth/reset-password', {
                userId,
                token,
                newPassword: password,
            });
            setSuccess(true);
            setTimeout(() => navigate('/login', { replace: true }), 2000);
        } catch (err: unknown) {
            let msg = tx(t, 'page.resetPassword.failed', 'Could not reset password. The link may be expired.');
            if (err && typeof err === 'object' && err !== null && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string }; status?: number } }).response;
                if (data?.status === 429) {
                    msg = tx(t, 'error.toomanyattempts', 'Too many attempts. Please wait and try again.');
                } else if (data?.data?.message) {
                    msg = data.data.message;
                }
            }
            setError(msg);
            scrollToFirstErrorField();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-dvh items-center justify-center px-4 py-8 sm:py-12">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md sm:p-8">
                <h1 className="mb-2 text-2xl font-bold">
                    {tx(t, 'page.resetPassword.title', 'Set a new password')}
                </h1>
                <p className="mb-6 text-sm text-gray-600">
                    {tx(t, 'page.resetPassword.subtitle', 'Choose a strong password for your shop account.')}
                </p>

                {linkInvalid && (
                    <div className="space-y-4">
                        <ValidationError
                            error={tx(
                                t,
                                'page.resetPassword.invalidLink',
                                'This reset link is invalid or incomplete.'
                            )}
                        />
                        <Link to="/forgot-password" className="underline text-[var(--accent-color)]">
                            {tx(t, 'page.resetPassword.requestNew', 'Request a new reset link')}
                        </Link>
                    </div>
                )}

                {!linkInvalid && success && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                        {tx(
                            t,
                            'page.resetPassword.success',
                            'Password updated. Redirecting you to login…'
                        )}
                    </div>
                )}

                {!linkInvalid && !success && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="password" className="mb-1 block text-sm font-medium">
                                {tx(t, 'page.resetPassword.newPassword', 'New password')}
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full rounded border p-2"
                                required
                                disabled={submitting}
                                autoComplete="new-password"
                            />
                        </div>
                        <div>
                            <label htmlFor="confirm" className="mb-1 block text-sm font-medium">
                                {tx(t, 'page.resetPassword.confirmPassword', 'Confirm password')}
                            </label>
                            <input
                                id="confirm"
                                name="confirm"
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="w-full rounded border p-2"
                                required
                                disabled={submitting}
                                autoComplete="new-password"
                            />
                        </div>
                        <ValidationError error={error} />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded bg-[var(--accent-color)] px-4 py-2 font-medium text-white disabled:opacity-60"
                        >
                            {submitting
                                ? tx(t, 'state.loading', 'Saving…')
                                : tx(t, 'page.resetPassword.submit', 'Reset password')}
                        </button>
                        <p className="text-center text-sm text-gray-600">
                            <Link to="/login" className="underline">
                                {tx(t, 'page.forgotPassword.backToLogin', 'Back to login')}
                            </Link>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
